import { useState } from "react";
import {
  useBannedCustomers,
  useUnbanCustomer,
  useUpdateBan,
  useBanCustomer,
  useNotifyBannedCustomer,
} from "@/hooks/use-banned-customers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldBan, Search, Loader2, Plus, MailCheck, MailWarning, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/utils";
import { TablePagination } from "@/components/ui/table-pagination";

export function BannedCustomersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editTarget, setEditTarget] = useState<{ id: string; reason: string } | null>(null);
  const [editReason, setEditReason] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", reason: "", notify: true });

  const { data, isLoading } = useBannedCustomers(search || undefined, page);
  const unbanMutation = useUnbanCustomer();
  const updateMutation = useUpdateBan();
  const banMutation = useBanCustomer();
  const notifyMutation = useNotifyBannedCustomer();

  const totalPages = data ? Math.ceil(data.total / 25) : 0;

  async function handleUnban(id: string) {
    try {
      await unbanMutation.mutateAsync(id);
      toast.success("Ban uklonjen");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri uklanjanju bana");
    }
  }

  async function handleAdd() {
    if (!addForm.email.trim() && !addForm.phone.trim()) {
      toast.error("Email ili telefon je obavezan");
      return;
    }
    const wantsNotify = addForm.notify && !!addForm.email.trim();
    try {
      const entry = await banMutation.mutateAsync({
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        name: addForm.name.trim(),
        reason: addForm.reason.trim() || undefined,
        notify: wantsNotify,
      });
      if (wantsNotify && entry?.notifyError) {
        toast.warning(`Kupac banovan, ali email nije poslan: ${entry.notifyError}`);
      } else if (wantsNotify) {
        toast.success("Kupac banovan i obaviješten");
      } else {
        toast.success("Kupac banovan");
      }
      setAddOpen(false);
      setAddForm({ name: "", email: "", phone: "", reason: "", notify: true });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri banovanju");
    }
  }

  async function handleNotify(id: string) {
    try {
      const entry = await notifyMutation.mutateAsync(id);
      if (entry?.notifyError) {
        toast.error(`Email nije poslan: ${entry.notifyError}`);
      } else {
        toast.success("Obavještenje poslano");
      }
    } catch (e: any) {
      toast.error(e?.message || "Greška pri slanju obavještenja");
    }
  }

  async function handleUpdateReason() {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, reason: editReason });
      toast.success("Razlog ažuriran");
      setEditTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri ažuriranju razloga");
    }
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <ShieldBan className="h-4 w-4" />
        <h2 className="text-sm font-medium">Banovani kupci</h2>
        {data && (
          <span className="text-xs text-muted-foreground">({data.total})</span>
        )}
        <Button size="sm" className="ml-auto" onClick={() => setAddOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Dodaj ban
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Pretraži po imenu, emailu ili telefonu..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="pl-8"
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 text-left font-medium">Ime</th>
                <th className="p-2 text-left font-medium">Email</th>
                <th className="p-2 text-left font-medium hidden sm:table-cell">Telefon</th>
                <th className="p-2 text-left font-medium hidden md:table-cell">Razlog</th>
                <th className="p-2 text-left font-medium hidden lg:table-cell">Banovao</th>
                <th className="p-2 text-left font-medium hidden sm:table-cell">Datum</th>
                <th className="p-2 text-left font-medium">Obavještenje</th>
                <th className="p-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((ban) => (
                <tr key={ban.id} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="p-2 font-medium">{ban.name || "—"}</td>
                  <td className="p-2 text-muted-foreground">{ban.email || "—"}</td>
                  <td className="p-2 text-muted-foreground hidden sm:table-cell">{ban.phone || "—"}</td>
                  <td
                    className="p-2 text-muted-foreground hidden md:table-cell cursor-pointer hover:text-foreground"
                    onClick={() => { setEditTarget({ id: ban.id, reason: ban.reason }); setEditReason(ban.reason); }}
                  >
                    {ban.reason || <span className="italic">Dodaj razlog...</span>}
                  </td>
                  <td className="p-2 text-muted-foreground hidden lg:table-cell">{ban.bannedBy}</td>
                  <td className="p-2 text-muted-foreground hidden sm:table-cell">
                    {formatDateTime(new Date(ban.bannedAt * 1000).toISOString())}
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      {ban.notifiedAt ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <MailCheck className="h-3.5 w-3.5" />
                          <span className="hidden md:inline">
                            {formatDateTime(new Date(ban.notifiedAt * 1000).toISOString())}
                          </span>
                        </span>
                      ) : ban.notifyError ? (
                        <span className="flex items-center gap-1 text-destructive" title={ban.notifyError}>
                          <MailWarning className="h-3.5 w-3.5" />
                          <span className="hidden md:inline truncate max-w-40">{ban.notifyError}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Nije slano</span>
                      )}
                      {ban.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5"
                          title={ban.notifiedAt ? "Pošalji ponovo" : "Pošalji obavještenje"}
                          onClick={() => handleNotify(ban.id)}
                          disabled={notifyMutation.isPending}
                        >
                          <Mail className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="p-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnban(ban.id)}
                      disabled={unbanMutation.isPending}
                    >
                      Ukloni
                    </Button>
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground">
                    {search ? "Nema rezultata" : "Nema banovanih kupaca"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj ban</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Input
              placeholder="Ime i prezime"
              value={addForm.name}
              onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Input
              placeholder="Email"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
            />
            <Input
              placeholder="Telefon"
              value={addForm.phone}
              onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <Input
              placeholder="Razlog (opcionalno)"
              value={addForm.reason}
              onChange={(e) => setAddForm((f) => ({ ...f, reason: e.target.value }))}
            />
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={addForm.notify && !!addForm.email.trim()}
                disabled={!addForm.email.trim()}
                onCheckedChange={(v) => setAddForm((f) => ({ ...f, notify: v === true }))}
              />
              <span>
                <span className="font-medium">Pošalji obavještenje kupcu</span>
                <span className="block text-muted-foreground">
                  {addForm.email.trim()
                    ? "Obavještenje o budućim narudžbama uz avansno plaćanje. Razlog bana se ne navodi."
                    : "Potreban je email kupca."}
                </span>
              </span>
            </label>
            <p className="text-xs text-muted-foreground">Email ili telefon je obavezan.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Otkaži</Button>
            <Button onClick={handleAdd} disabled={banMutation.isPending}>
              {banMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
              Banuj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uredi razlog</DialogTitle>
          </DialogHeader>
          <Input
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="Razlog za ban..."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Otkaži</Button>
            <Button onClick={handleUpdateReason} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
              Spremi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
